import {useMemo} from 'react';

import {type IssuesTimestampsByMetricQueryQuery} from '../../generated/graphql';
import {type ApolloError, useQuery} from '../../state/graphql/apolloWithAbort';
import {propagateErrorsContext} from '../../util/errors';
import {
  buildIssueTimestampsBucketedHistorySpec,
  convertBucketedHistoryToTimestampTuples,
} from './convertBucketedHistoryToTimestampTuples';
import {ISSUES_TIMESTAMPS_BY_METRIC_QUERY} from './issuesTimestamps.query';
import {type TimestampKeyTuple} from './types';
import {convertZoomToCacheKey} from './utils';

type UseIssueTimestampsOpts = {
  entityName: string;
  projectName: string;
  runName?: string;
  runCreatedAt?: string;
  xAxis: string;
  zoom: [number | null, number | null];
  skip?: boolean;
};

export function useIssueTimestamps({
  entityName,
  projectName,
  runName,
  runCreatedAt,
  xAxis,
  zoom,
  skip = false,
}: UseIssueTimestampsOpts): {
  keyByTimestamp: TimestampKeyTuple[];
  loading: boolean;
  error?: ApolloError;
} {
  const safeXAxis = xAxis === '_absolute_runtime' ? '_runtime' : xAxis;
  const bucketedHistorySpec = useMemo(
    () => buildIssueTimestampsBucketedHistorySpec({xAxis, zoom}),
    [xAxis, zoom]
  );

  const {data, loading, error} = useQuery<IssuesTimestampsByMetricQueryQuery>(
    ISSUES_TIMESTAMPS_BY_METRIC_QUERY,
    {
      variables: {
        entityName,
        projectName,
        runName: runName ?? '',
        bucketedHistorySpecs: [bucketedHistorySpec],
      },
      skip: skip || !entityName || !projectName || !runName,
      context: propagateErrorsContext(),
    }
  );

  const keyByTimestamp = useMemo(() => {
    const history = data?.project?.run?.bucketedHistory;
    if (!history || history.length !== 1) {
      return [];
    }

    const propTarget = xAxis === '_step' ? 'Step' : 'X';
    const zoomObj: Record<string, number> = {};
    if (zoom[0] != null) {
      zoomObj[`min${propTarget}`] = Math.floor(zoom[0]);
    }
    if (zoom[1] != null) {
      zoomObj[`max${propTarget}`] = Math.ceil(zoom[1]);
    }

    return convertBucketedHistoryToTimestampTuples({
      cacheKey: convertZoomToCacheKey(xAxis, zoom),
      creationTimestamp: new Date(runCreatedAt ?? 0).getTime(),
      history: history[0] as Array<
        Record<string, number | undefined> & {_timestampLast: number}
      >,
      safeXAxis,
      xAxis,
      zoom: zoomObj,
    });
  }, [data, runCreatedAt, safeXAxis, xAxis, zoom]);

  return {keyByTimestamp, loading, error};
}
